import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { selectCurrentRanking } from '@/lib/assignment/rankingAuditRecord';

export interface AssignmentAuditRecord {
  id: string;
  ticket_id: string;
  tech_id: string;
  tech_name: string;
  tech_skill_tier: number | null;
  selected: boolean;
  fitness_score: number | null;
  avg_proficiency: number | null;
  avg_skill_level: number | null;
  proficiency_score: number | null;
  workload_score: number | null;
  efficiency_score: number | null;
  idle_gap_score: number | null;
  comeback_bonus: number | null;
  specialist_penalty: number | null;
  tier_gap_penalty: number | null;
  complexity_weight: number | null;
  diagnostic_quality_bonus: number | null;
  bay_match: boolean | null;
  buffer_impact_minutes: number | null;
  learned_time_minutes: number | null;
  learned_time_source: string | null;
  tech_dcs: number | null;
  rejection_reason: string | null;
  explainability_details: Record<string, unknown> | null;
  created_at: string;
  /** Core-100 R5 (CORE-AB S): server provenance — null on rows written before R5. */
  ranking_run_id?: string | null;
  source?: string | null;
  actor_user_id?: string | null;
}

export interface OverrideInfo {
  isOverridden: boolean;
  systemRecommendedTech: string | null;
  systemRecommendedTechId: string | null;
  finalAssignedTechId: string | null;
}

interface UseTicketRankingResult {
  records: AssignmentAuditRecord[];
  overrideInfo: OverrideInfo;
  isLoading: boolean;
  error: Error | null;
}

export function useTicketRanking(ticketId: string | null): UseTicketRankingResult {
  // Fetch assignment audit records for the ticket
  const auditQuery = useQuery({
    queryKey: ['ticket-ranking', ticketId],
    queryFn: async () => {
      if (!ticketId) return [];
      
      const { data, error } = await supabase
        .from('assignment_audit')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('fitness_score', { ascending: false }); // Higher score = better match, show best first
      
      if (error) throw error;
      return (data || []) as AssignmentAuditRecord[];
    },
    enabled: !!ticketId,
  });

  // Fetch ticket's current assigned tech to detect overrides
  const ticketQuery = useQuery({
    queryKey: ['ticket-assigned-tech', ticketId],
    queryFn: async () => {
      if (!ticketId) return null;
      
      const { data, error } = await supabase
        .from('tickets')
        .select('assigned_tech_id')
        .eq('id', ticketId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!ticketId,
  });

  // Core-100 R5 (CORE-AB S): one ranking — the latest server run, the copy attached with its selection preferred.
  // Sort by overall_score DESC (from explainability_details) to match OptiPlace display order
  const records = selectCurrentRanking(auditQuery.data || []).sort((a, b) => {
    const aDetails = (a.explainability_details || {}) as Record<string, unknown>;
    const bDetails = (b.explainability_details || {}) as Record<string, unknown>;
    const aScore = typeof aDetails.overall_score === 'number' ? aDetails.overall_score : (a.fitness_score ?? 0);
    const bScore = typeof bDetails.overall_score === 'number' ? bDetails.overall_score : (b.fitness_score ?? 0);
    return bScore - aScore || String(a.tech_id).localeCompare(String(b.tech_id));
  });
  const systemPick = records.find(r => r.selected);
  const currentAssignedTechId = ticketQuery.data?.assigned_tech_id || null;

  const overrideInfo: OverrideInfo = {
    isOverridden: !!(systemPick && currentAssignedTechId && systemPick.tech_id !== currentAssignedTechId),
    systemRecommendedTech: systemPick?.tech_name || null,
    systemRecommendedTechId: systemPick?.tech_id || null,
    finalAssignedTechId: currentAssignedTechId,
  };

  return {
    records,
    overrideInfo,
    isLoading: auditQuery.isLoading || ticketQuery.isLoading,
    error: auditQuery.error || ticketQuery.error,
  };
}
